import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';

/**
 * CameraTestComponent
 *
 * This component is for testing camera functionality. It provides:
 * - A button to open the camera and take a photo
 * - A button to open the camera and scan a QR code/barcode
 *
 * Uses @zxing/ngx-scanner for barcode/QR scanning (battle-tested ZXing library)
 * This is a testing/development component.
 */
@Component({
  selector: 'app-camera-test',
  templateUrl: './camera-test.component.html',
  styleUrls: ['./camera-test.component.css'],
})
export class CameraTestComponent implements OnInit, OnDestroy {
  @ViewChild('scanner') scanner?: ZXingScannerComponent;
  @ViewChild('videoElement') videoElement?: ElementRef;
  @ViewChild('canvasElement') canvasElement?: ElementRef;

  public isPhotoMode = false;
  public isQrMode = false;
  public capturedPhoto: string | null = null;
  public detectedQrCode: string | null = null;
  public stream: MediaStream | null = null;
  public statusMessage = '';
  public hasPermission = false;
  public qrFormats = [BarcodeFormat.QR_CODE];

  constructor(private modalController: ModalController) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.closeCamera();
  }


  // #region QR Scanner Configuration

/**
 * Video constraints for the QR scanner stream.
 *
 * Requests the rear ('environment') camera at 1080p with a 720p floor.
 * Higher resolution is the primary driver of scan working distance: a
 * ~1.5" QR code that only decoded within ~6" at default resolution
 * decodes considerably farther once the stream is 1080p+, because the
 * decoder gets enough pixels-per-module to lock on.
 */
public videoConstraints: MediaTrackConstraints = {
  facingMode: { ideal: 'environment' },
  width: { min: 1280, ideal: 1920 },
  height: { min: 720, ideal: 1080 },
};

/**
 * Enables ZXing's more exhaustive decode pass. Improves detection of
 * small, angled, or partially blurred codes at the cost of extra CPU
 * per frame. Acceptable here since QR is the only active format.
 */
public tryHarder = true;

/** Device selected for scanning; bound to the scanner via [device]. */
public selectedDevice?: MediaDeviceInfo;

// #endregion

/**
 * Opens native camera for photo capture using file input
 */
openCameraForPhoto(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.capture = 'environment';

  input.onchange = (event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.capturedPhoto = e.target?.result as string;
        this.statusMessage = 'Photo captured!';
      };
      reader.readAsDataURL(file);
    }
    input.remove();
  };

  input.oncancel = () => input.remove();
  input.click();
}

  /**
   * Opens camera for QR code scanning using ZXing scanner
   */
  async openCameraForQrScan(): Promise<void> {
    try {
      this.statusMessage = 'Opening camera for QR scan...';
      this.isQrMode = true;
      this.isPhotoMode = false;
      this.capturedPhoto = null;
      this.detectedQrCode = null;

      setTimeout(() => {
        this.statusMessage = 'Camera ready - point at QR code to scan';
      }, 500);
    } catch (error) {
      this.statusMessage = `Camera error: ${error}`;
      this.isQrMode = false;
    }
  }

  /**
   * Handles QR code detection result from ZXing scanner
   */
  onQrCodeResult(result: string): void {
    if (result) {
      this.detectedQrCode = result;
      this.statusMessage = `QR Code detected: ${result}`;
      this.isQrMode = false;
      if (this.scanner) {
        this.scanner.reset();
      }
    }
  }

  /**
   * Captures the current frame from the camera
   */
  capturePhoto(): void {
    if (this.videoElement && this.canvasElement && this.stream) {
      const video = this.videoElement.nativeElement;
      const canvas = this.canvasElement.nativeElement;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);

      this.capturedPhoto = canvas.toDataURL('image/jpeg');
      this.statusMessage = 'Photo captured!';
    }
  }

  /**
   * Closes the camera stream
   */
  closeCamera(): void {
    if (this.scanner) {
      this.scanner.reset();
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.isPhotoMode = false;
    this.isQrMode = false;
    this.statusMessage = '';
  }

  /**
   * Retakes a photo
   */
  retakePhoto(): void {
    this.capturedPhoto = null;
    this.statusMessage = 'Camera ready - tap to capture';
  }

  /**
   * Closes the modal
   */
  close(): void {
    this.closeCamera();
    this.modalController.dismiss();
  }

  /**
   * Closes the modal and returns the captured photo
   */
  usePhoto(): void {
    if (this.capturedPhoto) {
      this.closeCamera();
      this.modalController.dismiss({ photo: this.capturedPhoto });
    }
  }




  public cameraReady = false;

/**
 * Handles the list of cameras discovered by the scanner.
 *
 * Prefers the main rear ('Back Camera') lens and explicitly avoids the
 * ultra-wide/telephoto lenses on multi-camera iPhones/iPads, since the
 * ultra-wide produces soft images on small, close subjects like a
 * 1.5" QR code. Falls back to the first rear camera, then the first
 * available device.
 *
 * @param devices Cameras reported by the ZXing scanner.
 */
onCamerasFound(devices: MediaDeviceInfo[]): void {
  this.cameraReady = devices && devices.length > 0;

  if (!this.cameraReady) {
    this.statusMessage = 'No camera found';
    return;
  }

  const rearCameras = devices.filter((d) => /back|rear|environment/i.test(d.label));
  const avoidLenses = /ultra|wide angle|telephoto/i;

  this.selectedDevice =
    rearCameras.find((d) => /back camera/i.test(d.label) && !avoidLenses.test(d.label)) ??
    rearCameras.find((d) => !avoidLenses.test(d.label)) ??
    rearCameras[0] ??
    devices[0];

  this.statusMessage = 'Point camera at a QR code';
}

onPermission(granted: boolean): void {
  if (!granted) {
    this.statusMessage = 'Camera permission denied';
    this.isQrMode = false;
  }
}

onScanError(error: any): void {
  // fires frequently while scanning — only update message on real errors
  if (error?.name === 'NotFoundException') return;
  this.statusMessage = `Scan error: ${error}`;
}

onScanComplete(result: any): void {
  // fires on every frame attempt — use to show "still looking" feedback
  if (!this.detectedQrCode) {
    this.statusMessage = 'Scanning... hold steady';
  }
}



}